<script lang="ts">
  import { onMount } from 'svelte';
  import { useLocation, useNavigate } from 'svelte-navigator';
  import { _ } from 'svelte-i18n';

  import { AppState, SetIconsBackground, ToastQueue } from '../etc/stores';

  import { extractMessageFromError } from '../services/api';
  import { Auth } from '../services/auth';
  import { Logger } from '../services/logger';
  import Analytics from '../services/analytics';

  import Page from '../components/misc/Page.svelte';
  import Text from '../components/misc/Text.svelte';
  import Button from '../components/form/Button.svelte';
  import BackArrow from '../components/misc/BackArrow.svelte';
  import ArrowRight from '../components/misc/ArrowRight.svelte';
  import LockIndicator from '../components/icons/LockIndicator.svelte';
  import { Container } from '../services/container';
  import Link from '../components/misc/Link.svelte';
  import { replaceVariables } from '../i18n';
  import Heading from '../components/typography/Heading.svelte';
  import Label from '../components/typography/Label.svelte';
  import flags from '../etc/flagKeys';

  const navigate = useNavigate();
  const location = useLocation();
  const auth = Container.get(Auth);
  const logger = Container.get(Logger).context(
    'ConnectAccount.svelte'
  ) as Logger;

  const isSelectAccount = $location.state.selectAccount;
  const { providerData = {} } = $AppState.auth;

  const { phone } = providerData;

  const firstName = providerData.firstName || $location.state.userData?.name;
  const lastName = providerData.lastName || $location.state.userData?.lastname;
  const email = providerData.email || $location.state.userData?.email;

  const isCustomerImportFlow = $AppState.website.authFlow === 'customer-import';

  const hideBackInFlows = ['customer-import'];
  const hideAlreadyHaveAnAccountLinkInFlows = ['customer-import'];

  let loading = false;

  const t = (text: string, values?: any) => {
    return replaceVariables($_(`connect_account.${text}`), values);
  };

  const signUp = async () => {
    if (!firstName || !lastName || !email) {
      navigate('/account-name', { state: { email } });
      return;
    }

    try {
      loading = true;

      await auth.signup({
        email,
        firstName,
        lastName,
        phone
      });

      await auth.finishAuthFlow(navigate);
    } catch (e) {
      logger.error(e);

      ToastQueue.add({
        title: $_('error.title'),
        message: extractMessageFromError(e),
        type: 'error',
        duration: 5000
      });
    } finally {
      loading = false;
    }
  };

  const getTitleKeyByAuthFlow = () => {
    const titleMapper: Record<string, string> = {
      'customer-import': 'customer_import_title'
    };

    if (!titleMapper[$AppState.website.authFlow]) {
      return 'title';
    }

    return titleMapper[$AppState.website.authFlow];
  };

  const getDescription = () => {
    if (isCustomerImportFlow) {
      return 'customer_import_description';
    }

    if (isSelectAccount) {
      const { signInOptions } = $AppState.auth;

      const isOptionsFromTheSameAccount =
        !signInOptions?.length ||
        signInOptions.every(
          option => option.customerId === signInOptions[0].customerId
        );

      if (isOptionsFromTheSameAccount) {
        return 'description_select.same_account';
      }

      return 'description_select.diff_account';
    }

    return 'description';
  };

  const onLink = () => {
    navigate('/connect-email');
  };

  onMount(() => {
    SetIconsBackground('light');
    Analytics.logPageView(
      `Connect Account${Analytics.getFreeCoinsTitleFlow()}`,
      '/connect-account'
    );
  });
</script>

<Page className="connectAccount__container" padding="80px 24px 16px">
  {#if !hideBackInFlows.includes($AppState.website.authFlow)}
    <BackArrow />
  {/if}
  <LockIndicator />
  <div class="connectAccount__title">
    <Heading type="h2" textAlign="center">{t(getTitleKeyByAuthFlow())}</Heading>
  </div>
  <div class="connectAccount__description">
    <Label type="default-regular" textAlign="center" color="#7A89B0">
      {@html t(getDescription(), {
        EMAIL: email,
        businessName: $AppState.business.name
      })}
    </Label>
  </div>
  <div class="connectAccount__button">
    <Button type="button" on:click={signUp} {loading}>
      <p>{t('btn')}</p>
      <ArrowRight />
    </Button>
  </div>
  {#if !hideAlreadyHaveAnAccountLinkInFlows.includes($AppState.website.authFlow)}
    <div class="connectAccount__link">
      <Link fontSize="14px" lineHeight="20px" on:click={onLink}
        >{t('link')}</Link
      >
    </div>
  {/if}
  <div class="connectAccount__disclaimer">
    <Text fontSize="12px" lineHeight="20px" color="#92A2B0">
      {@html t('disclaimer')}
    </Text>
  </div>
</Page>

<style lang="scss">
  :global(.connectAccount__container) {
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  div {
    width: 100%;
  }
  .connectAccount {
    &__title {
      margin-top: 32px;
    }
    &__description {
      margin-top: 8px;

      :global(p) {
        line-height: 24px;
      }
    }

    &__button {
      margin-top: 48px;

      p {
        margin-right: 16px;
        font-weight: 600;
        font-size: 15px;
        line-height: 16px;
      }
    }

    &__link {
      margin-top: 24px;
    }

    &__disclaimer {
      margin-top: 42px;
      max-width: 272px;
    }

    &__disclaimer a {
      text-decoration: underline;
      color: var(--twism-theme-text);
      max-width: 272px;
    }
  }
</style>
